import { find, isNil, merge, orderBy } from 'lodash-es'

import { DealerAPI, EnterpriseAPI, KpiAPI } from 'src/api'
import $router from 'src/router'
import $store from 'src/store'

const enterpriseModule = {
  namespaced: true,
  state: {
    defaultPageSettings: {
      /*
      Base object of pageSettings, contains required properties for page.
      Created to prevent difference between vuex and localhost obj after code change
      */
      orderBy: '',
      dealerships: [],
      comparisonMetrics: [],
      comparisonPercentages: [],
    },

    favoriteMetrics: [],
    currentReport: localStorage.getItem('enterpriseCurrentReport'),

    pageSettings: JSON.parse(localStorage.getItem('enterprisePageSettings')) || {},
  },
  mutations: {
    CLEAR_STORE(state) {
      state.favoriteMetrics = []
    },

    SET_CURRENT_REPORT(state, payload) {
      state.currentReport = payload

      localStorage.setItem('enterpriseCurrentReport', payload)
      if (payload) {
        $router.toggleQueryParams({ add: { reportId: payload } })
      }
    },

    ADD_FAVORITE_METRIC(state, payload) {
      state.favoriteMetrics = [...state.favoriteMetrics, payload]
    },
    REMOVE_FAVORITE_METRIC(state, payload) {
      state.favoriteMetrics = state.favoriteMetrics.filter((e) => e !== payload)
    },
    SET_FAVORITE_METRICS(state, payload) {
      state.favoriteMetrics = payload
    },
    SET_PAGE_DEALERSHIPS(state, payload) {
      state.pageSettings[$store.state.settings.dealerGroupId].dealerships = payload

      localStorage.setItem('enterprisePageSettings', JSON.stringify(state.pageSettings))
    },
    ADD_PAGE_DEALERSHIP(state, payload) {
      state.pageSettings[$store.state.settings.dealerGroupId].dealerships = [
        ...state.pageSettings[$store.state.settings.dealerGroupId].dealerships,
        payload,
      ]

      localStorage.setItem('enterprisePageSettings', JSON.stringify(state.pageSettings))
    },
    REMOVE_PAGE_DEALERSHIP(state, payload) {
      state.pageSettings[$store.state.settings.dealerGroupId].dealerships = state.pageSettings[
        $store.state.settings.dealerGroupId
      ].dealerships.filter((e) => e !== payload)

      localStorage.setItem('enterprisePageSettings', JSON.stringify(state.pageSettings))
    },
    ADD_PAGE_PERCENTAGE(state, payload) {
      state.pageSettings[$store.state.settings.dealerGroupId].comparisonPercentages.push(payload)

      localStorage.setItem('enterprisePageSettings', JSON.stringify(state.pageSettings))
    },
    REMOVE_PAGE_PERCENTAGE(state, payload) {
      state.pageSettings[$store.state.settings.dealerGroupId].comparisonPercentages =
        state.pageSettings[$store.state.settings.dealerGroupId].comparisonPercentages.filter(
          (e) => e !== payload
        )

      localStorage.setItem('enterprisePageSettings', JSON.stringify(state.pageSettings))
    },
    SET_PAGE_ORDERBY(state, payload) {
      state.pageSettings[$store.state.settings.dealerGroupId].orderBy = payload

      localStorage.setItem('enterprisePageSettings', JSON.stringify(state.pageSettings))
    },
    SET_PAGE_SETTINGS(state, payload) {
      localStorage.setItem(
        'enterprisePageSettings',
        JSON.stringify({
          ...state.pageSettings,
          [$store.state.settings.dealerGroupId]: payload,
        })
      )

      state.pageSettings = JSON.parse(localStorage.getItem('enterprisePageSettings'))
    },
    ADD_COMPARISON(state, payload) {
      state.pageSettings[$store.state.settings.dealerGroupId].comparisonMetrics.push(payload)

      localStorage.setItem('enterprisePageSettings', JSON.stringify(state.pageSettings))
    },
    REMOVE_COMPARISON(state, payload) {
      state.pageSettings[$store.state.settings.dealerGroupId].comparisonMetrics =
        state.pageSettings[$store.state.settings.dealerGroupId].comparisonMetrics.filter(
          (e) => e !== payload
        )

      localStorage.setItem('enterprisePageSettings', JSON.stringify(state.pageSettings))
    },
  },
  getters: {
    dealerSettings(state) {
      return state.pageSettings[$store.state.settings.dealerGroupId]
    },
    hasCurrentReport(state) {
      return !isNil(state.currentReport) && state.currentReport !== ''
    },
  },
  actions: {
    async getMetrics({ commit }) {
      const favorite = await KpiAPI.getMy({ type: 1 })

      commit('SET_FAVORITE_METRICS', favorite.ungrouped)

      return true
    },
    async addReport(
      { dispatch, getters, state, commit },
      { isGlobal, name, reportId, rename = false }
    ) {
      let postData = {},
        newReportId

      if (rename) {
        postData = {
          name,
          isGlobal,
        }
      } else {
        const metrics = state.favoriteMetrics.map((e) => ({
            kpi: e,
            comparison: getters.dealerSettings.comparisonMetrics.includes(e) ? 1 : 0,
            percentages: getters.dealerSettings.comparisonPercentages.includes(e) ? 1 : 0,
          })),
          dealerships = getters.dealerSettings.dealerships,
          orderBy =
            getters.dealerSettings.orderBy === '' ? 'dealerships' : getters.dealerSettings.orderBy

        postData = {
          isGlobal,
          name,
          dealerships,
          metrics,
          orderBy,
        }
      }

      if (reportId) {
        const data = await EnterpriseAPI.updateReport({
          ...postData,
          reportId,
        })

        if (data.errors) {
          return data
        }
      } else {
        const data = (await EnterpriseAPI.addReport(postData)).report

        if (data.errors) {
          return data
        }

        newReportId = data.id
      }

      if (!reportId) {
        commit('SET_CURRENT_REPORT', newReportId)
        dispatch('updatePageSettingsByReport', { reports: state.reports })
      }

      return true
    },
    getPageSettingsByReport({ state, rootGetters }, reports) {
      const settings = JSON.parse(JSON.stringify(state.defaultPageSettings))
      const reportSettings = find(reports, ['id', +state.currentReport])

      if (!reportSettings) {
        return null
      }
      settings.dealerships = rootGetters['dealer/currentDealerships']
      settings.orderBy = reportSettings.orderBy === 'dealerships' ? '' : reportSettings.orderBy

      const favoriteMetrics = [
        ...reportSettings.metrics.map((e) => {
          if (e.comparison === 1) {
            settings.comparisonMetrics.push(e.kpi)
          }
          if (e.percentages === 1) {
            settings.comparisonPercentages.push(e.kpi)
          }

          return e.kpi
        }),
      ]

      return {
        settings,
        favoriteMetrics,
      }
    },
    async updatePageSettingsByReport({ commit, dispatch }, { reports, allowSetDefault = true }) {
      const settings = await dispatch('getPageSettingsByReport', reports)

      if (!settings && allowSetDefault) {
        dispatch('setDefaultPageSettings')
        return
      } else if (settings) {
        commit('SET_FAVORITE_METRICS', settings.favoriteMetrics)
        commit('SET_PAGE_SETTINGS', settings.settings)
      }
    },
    async getMetric({ metric }) {
      const data = await KpiAPI.get({ metric })
      return data
    },
    async getTrend({ metric }) {
      const data = await KpiAPI.getTrend({ metric })
      return data.trend.trend
    },
    exportReport({ state }, body) {
      const { favoriteMetrics } = state

      const data = EnterpriseAPI.export({ ...body, metrics: favoriteMetrics })

      return data
    },
    async getMetricsData(
      { rootState, state },
      {
        metric, // Metric, which data must be downloaded
        dealerships = null, // If passed, download metric data only for this dealerships
        lastMonth = false, // lastMonth/lastQuarter metric data for comparison
        lastYear = false, // lastYear metric data for comparison
      }
    ) {
      const { dealerGroupId, viewType } = rootState.settings

      let period

      switch (
        true // Calculating period
      ) {
        case lastYear:
          period = viewType === 'monthsOfYear' ? 'previous' : 'lastYear'
          break
        case lastMonth:
          period = 'previous'
          break
        default:
          period = 'current'
          break
      }

      const data = await KpiAPI.getEnterprise({
        metric,
        dealerships: dealerships || state.pageSettings[dealerGroupId].dealerships,
        period,
      })

      return data
    },
    addFavoriteMetric({ commit }, metric) {
      commit('ADD_FAVORITE_METRIC', metric)
    },
    removeFavoriteMetric({ commit }, metric) {
      commit('REMOVE_FAVORITE_METRIC', metric)
    },
    async restoreMetrics({ commit }) {
      const data = await KpiAPI.restoreDefaults({ type: 1 })
      commit('SET_FAVORITE_METRICS', data.favorites.ungrouped)

      return data
    },
    async moveMetric({ commit, state }, { oldIndex, newIndex }) {
      const newArr = [...state.favoriteMetrics]

      if (newIndex >= newArr.length) {
        let k = newIndex - newArr.length + 1
        while (k--) {
          newArr.push(undefined)
        }
      }
      newArr.splice(newIndex, 0, newArr.splice(oldIndex, 1)[0])

      commit('SET_FAVORITE_METRICS', newArr)
    },
    async setDefaultPageSettings({ commit, rootGetters, rootState, state }) {
      const settings = JSON.parse(JSON.stringify(state.defaultPageSettings))

      if (rootGetters['dealer/dealerships'].length !== 0) {
        settings.dealerships = rootGetters['dealer/currentDealerships']
      } else {
        const data = await DealerAPI.getList()

        const dealer = find(data, {
          id: +rootState.settings.dealerGroupId,
        })

        settings.dealerships = dealer.dealerships.map((e) => e.id)
      }

      commit('SET_PAGE_SETTINGS', settings)
    },
    orderByMetric(
      { state, commit, rootState, getters, rootGetters },
      {
        metric, // (String)
        metricsData, // Metric data to calculate dealerships order (Obj)
        reset = false, // If true, resetting current ordering and returns to default ordering (Bool)
        initOrdering = false, // If true, just arrages dealerships with current orderBy metric from state (Bool)
      }
    ) {
      const { dealerGroupId } = rootState.settings
      if (reset) {
        commit(
          'SET_PAGE_ORDERBY',
          getters.dealerSettings.orderBy === 'dealership' ? 'alphabet' : 'dealership'
        )

        const dealerships =
          getters.dealerSettings.orderBy === 'alphabet'
            ? $store.getters['dealer/dealerships']
                .map((e) => ({ id: e.id, name: e.name }))
                .sort((a, b) => {
                  if (a.name < b.name) {
                    return -1
                  }
                  if (a.name > b.name) {
                    return 1
                  }
                  return 0
                })
                .map((e) => +e.id)
                .filter((id) =>
                  +rootState.settings.dealerShipId === 0
                    ? true
                    : rootGetters['dealer/currentDealerships'].includes(id)
                )
            : rootGetters['dealer/currentDealerships']

        commit('SET_PAGE_DEALERSHIPS', dealerships)

        return
      }

      const visibleMetricsData = metricsData.dealerships.filter((e) =>
        state.pageSettings[dealerGroupId].dealerships.includes(e.dealerShipId)
      ) // Filtering extra data (passed metricsData obj may content metrics for hidden dealerships)

      /* Ordering received metricsData by descending */
      const settingsOrderBy = state.pageSettings[dealerGroupId].orderBy
      let sortedDealerships = orderBy(visibleMetricsData, ['value'])

      if (!initOrdering) {
        commit(
          // Replacing orderBy with new metric
          'SET_PAGE_ORDERBY',
          metric === settingsOrderBy && settingsOrderBy[0] !== '-' && settingsOrderBy !== ''
            ? `-${metric}`
            : metric
        )
      }

      /* If orderBy metric contains "-" symbol at the beginning, sort by ascending, else sort by descending */
      if (state.pageSettings[dealerGroupId].orderBy[0] !== '-') {
        sortedDealerships = sortedDealerships.reverse()
      }

      commit(
        'SET_PAGE_DEALERSHIPS',
        sortedDealerships.map((e) => e.dealerShipId)
      )
    },
  },
}

export default merge({
  ...enterpriseModule,
})
